// ./src/utils/themeTokens.ts
//
// ROADMAP A2e #89 — the theme editor's token plumbing. Pure (no React), so `check-theme-editor` can run it.
//
// ⭐ "CHANGE THE ACCENT AND EVERY BUTTON FOLLOWS" — the point of the two-layer token model (api docs/THEMES.md). But the
// stored themes carry their role colours as LITERAL copies of the foundation (`button.primary.bg` = the accent, written
// out by the seed's `buildTokens`), and #89 is what makes the site draw them. So an editor that only wrote `color.accent`
// would leave every button on the OLD accent — the host changes the accent and the RSVP button does not move.
// `applyFoundationChange` keeps the copies linked: a role still holding the value the foundation had follows the new one;
// a role the host changed on purpose (a different value) keeps it. A role that is cleared falls back to the foundation on
// the site anyway.

import type { ThemeTokens } from "@/utils/api";

type Path = readonly string[];

/** Which stored role values are derived from each foundation key (the seed's `buildTokens`), by token path. */
export const THEME_FOLLOWERS: Record<string, Path[]> = {
  "color.accent": [
    ["button", "primary", "bg"],
    ["element", "link", "color"],
    ["element", "eyebrow", "color"],
  ],
  "color.accentInk": [["button", "primary", "text"]],
  "color.ink": [
    ["button", "secondary", "text"],
    ["element", "title", "color"],
    ["element", "nav", "activeText"],
  ],
  "color.lineStrong": [["button", "secondary", "border"]],
  "color.muted": [["element", "nav", "text"]],
  "color.surface": [["element", "card", "bg"]],
  "color.line": [["element", "card", "border"]],
  "color.accentWash": [["element", "featureBand", "bg"]],
  "color.accentLine": [["element", "featureBand", "border"]],
  "font.display": [["element", "title", "family"]],
};

const isPlainObject = (v: unknown): v is Record<string, any> => !!v && typeof v === "object" && !Array.isArray(v);

/** The value at a token path (`["color", "accent"]`), or undefined. */
export const getTokenPath = (tokens: unknown, path: Path): unknown => {
  let node: unknown = tokens;

  for (const key of path) {
    if (!isPlainObject(node)) {
      return undefined;
    }

    node = node[key];
  }

  return node;
};

/**
 * A copy of the tokens with `value` at `path` — or the key REMOVED for null / blank (the site's fallback then applies),
 * with any group left empty removed too. Never mutates its input.
 */
export const setTokenPath = (tokens: ThemeTokens | null | undefined, path: Path, value: unknown): ThemeTokens => {
  const root: Record<string, any> = isPlainObject(tokens) ? JSON.parse(JSON.stringify(tokens)) : {};
  const blank = value === null || value === undefined || (typeof value === "string" && !value.trim());
  const parents: Record<string, any>[] = [root];
  let node = root;

  for (const key of path.slice(0, -1)) {
    if (!isPlainObject(node[key])) {
      if (blank) {
        return root as ThemeTokens;
      }

      node[key] = {};
    }

    node = node[key];
    parents.push(node);
  }

  const last = path[path.length - 1];

  if (blank) {
    delete node[last];

    // Prune the groups the removal emptied, deepest first.
    for (let i = path.length - 2; i >= 0; i -= 1) {
      if (Object.keys(parents[i + 1]).length === 0) {
        delete parents[i][path[i]];
      }
    }
  } else {
    node[last] = value;
  }

  return root as ThemeTokens;
};

/**
 * Set a FOUNDATION value (`color.accent`, `font.display`, …) and carry the linked role values with it: each follower
 * that still equals the foundation's previous value takes the new one. Any other key is set on its own.
 */
export const applyFoundationChange = (tokens: ThemeTokens | null | undefined, dottedPath: string, value: string | null): ThemeTokens => {
  const path = dottedPath.split(".");
  const previous = getTokenPath(tokens, path);
  let next = setTokenPath(tokens, path, value);

  for (const follower of THEME_FOLLOWERS[dottedPath] ?? []) {
    const current = getTokenPath(next, follower);

    if (current !== undefined && current === previous) {
      next = setTokenPath(next, follower, value);
    }
  }

  return next;
};

// ── B20 (owner, 2026-10-15): one event's adjustments to its theme's LOOK ─────────────────────────────────────────────
// An event stores only what it CHANGES (`event.themeOverrides`, deep-merged over the theme by the server). The page edits
// the merged result and saves the difference — so switching themes keeps the adjustments, and "Reset" is removing one.
// ⓘ The #88 styling (`background`, `element.block`) shares the column but is edited by its own fields; these helpers
//   never touch it.

const SITE_STYLING_ROOTS: Path[] = [["background"], ["element", "block"]];

const isStyling = (path: string[]): boolean =>
  SITE_STYLING_ROOTS.some((root) => root.every((key, i) => path[i] === key));

/** `over` deep-merged onto `base` (objects merged, every other value replaced). Pure; never mutates. */
export const mergeThemeTokens = (base: unknown, over: unknown): ThemeTokens => {
  const merge = (a: unknown, b: unknown): unknown => {
    if (!isPlainObject(a) || !isPlainObject(b)) {
      return b === undefined ? a : b;
    }

    const out: Record<string, any> = { ...a };

    for (const [k, v] of Object.entries(b)) {
      out[k] = merge(a[k], v);
    }

    return out;
  };

  return JSON.parse(JSON.stringify(merge(isPlainObject(base) ? base : {}, isPlainObject(over) ? over : {}))) as ThemeTokens;
};

/** Every non-object value, keyed by its path. */
const leavesOf = (o: unknown, prefix: string[] = [], out: Array<[string[], unknown]> = []): Array<[string[], unknown]> => {
  if (!isPlainObject(o)) {
    return out;
  }

  for (const [k, v] of Object.entries(o)) {
    if (isPlainObject(v)) {
      leavesOf(v, [...prefix, k], out);
    } else {
      out.push([[...prefix, k], v]);
    }
  }

  return out;
};

/** The LOOK part of an event's stored overrides: everything but the #88 styling. Pure. */
export const themeLookOfOverrides = (overrides: unknown): ThemeTokens => {
  let out: ThemeTokens = {} as ThemeTokens;

  for (const [path, v] of leavesOf(overrides)) {
    if (!isStyling(path)) {
      out = setTokenPath(out, path, v);
    }
  }

  return out;
};

/**
 * What an event adjusts: every value of `effective` (the theme with the event's look over it, as edited) that differs
 * from the theme's own (`base`). The #88 styling is left out. A value equal to the theme's is not stored (Reset). Pure.
 */
export const themeLookDiff = (effective: unknown, base: unknown): ThemeTokens => {
  let out: ThemeTokens = {} as ThemeTokens;

  for (const [path, v] of leavesOf(effective)) {
    if (!isStyling(path) && JSON.stringify(v) !== JSON.stringify(getTokenPath(base, path))) {
      out = setTokenPath(out, path, v);
    }
  }

  return out;
};

/** The dotted paths an event adjusts (for the "adjusted" marks and Reset). Pure. */
export const themeLookPaths = (lookDiff: unknown): string[] => leavesOf(lookDiff).map(([path]) => path.join("."));
